import {
    Wrapper,
    SendButton,
    Subtitle,
    Title,
    PinCheckboxWrapper,
} from "./style";
import {FlexBox, useBreakpoint, useIsMobile, useToasts} from "@brg/ui";
import {act, BaseSyntheticEvent, FC, useCallback, useEffect, useMemo, useState} from "react";
import {API, Endpoints} from "@brg/api";
import {useTranslation} from "react-i18next";
import { ProfileUtils, useAssociatedProfile, useAuth } from "@brg/auth";
import {useSelector} from "react-redux";
import { Input } from "@brg/input";
import { LoaderIcon } from "shared/modals/artzLegalsModal/LoaderAnimation";
import { LayoutConst } from "core";
import { Availability } from "shared/pages/projectEdit/components/Availability/Availability";
import { AvailabilityAction } from "shared/pages/projectEdit/types";
import { copyLinkFolder } from "shared/helpers/copyLink";
import { useFoldersController } from "shared/hooks/useFoldersController";
import { ProfileFoldersProps, ProfileFoldersSelector } from "shared/reducers/ProfileFoldersSlice";
import { FOLDERS_MODE, FolderT } from "../../tabs/const";
import useQueryState from "shared/hooks/useQueryState";
import { InfoIcon } from "shared/components/InfoIcon/InfoIcon";

const defaultFolder = {
    name: '',
    accountId: '',
    description: '',
    isDefault: false,
    isHidden: false,
    isAvailableAtLink: AvailabilityAction.open,
} as FolderT

interface FolderModalProps {
    folderId?: string,
    activeFolder?: FolderT,
    callback?: (t: any) => void,
}

export const FolderEdit: FC<FolderModalProps> =
    ({folderId, activeFolder, callback }) => {
    const profile = useAssociatedProfile();
    const isMobile = useBreakpoint(LayoutConst.screen.m);
    const toasts = useToasts();
    const is744 = useBreakpoint(LayoutConst.screen.s - 1);
    const isTab = useBreakpoint(LayoutConst.screen.m);
    const { info } = useToasts();
    const {t, i18n} = useTranslation();
    const [{}, setQueryParams] = useQueryState();
    const currentFolder = useSelector(ProfileFoldersSelector)?.currentFolder;
    const [folder, setFolder] = useState<FolderT>(defaultFolder);
    const { 
        onFolderChange, 
        onCreate, 
        onEdit,
        onDelete 
    } = useFoldersController();

    const isDisabled = useMemo(() => folder?.name === '' , [folder])

    const onDataChange = (ev: BaseSyntheticEvent , field: string) => {
        setFolder({ ...folder, [field]: ev.target.value })

        if (activeFolder) {
            onEdit && onEdit({ ...folder, [field]: ev.target.value }, FOLDERS_MODE.edit, true).then((r) => {
                callback && callback(Date.now());
            })
        }
    }

    const onDeleteFolder = () => {

        onDelete(folder?.id ?? '', true)?.then(
            (r) => {
                callback && callback(Date.now())
                info(t('folders.modal.success_delete_folder', { name: folder.name }));
                onFolderChange(ProfileFoldersProps.projects)
                setQueryParams({ folderId: undefined })
            }
            
        )
    }

    const onAvailabilityChange = (val: string ) => {
        const compValue = val !== 'open';
        onDataChange({ target: { value: compValue }} as BaseSyntheticEvent, 'isHidden');
    }

    const onSubmit = () => {
        const data = {
            ...(folderId && { ...folder, id: folderId }),
            "name": folder.name,
            "description": '',
            "accountId": profile?.accountId,
            "isDefault": folder?.isDefault,
            "isHidden": folder?.isHidden
        } as FolderT

        if (currentFolder === ProfileFoldersProps.edit) {
            onEdit(data, FOLDERS_MODE.edit, true)
        } else {
            onCreate(data)
        }

        setTimeout(() => {
            info(t(`folders.modal.success_${folderId ? 'edit' : 'create'}`, { name: folder?.name || ''}));
            callback && callback(Date.now())
            onCancel && onCancel();
        } , 1000)

    }

    const onCancel = () => {
        onFolderChange(ProfileFoldersProps.projects)
    }

    const handleLinkCopy = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        copyLinkFolder(folder?.name, profile?.accountId || '', toasts, t);
    };

    useEffect(() => {
        if (activeFolder) {
            setFolder(activeFolder)
        }
    }, [activeFolder])

    const computeAvailability = useMemo(() => {
        if (!folder?.isHidden) return 'open';
        return 'link'
    } , [folder])

    return <Wrapper>
        <FlexBox justify="space-between" align={"center"} style={{marginRight: '5px', position: 'relative'}}>
            <Title>{folderId ?  t("folders.modal.edit_title") : t("folders.modal.title")}</Title>
        </FlexBox>
        <FlexBox direction="column" directionMobile="column" align={"start"} gy={8}>
            <Subtitle>{t("folders.modal.subtitle")}</Subtitle>
            <Input.Text 
                value={folder?.name ?? ''}
                onChange={(ev) => onDataChange(ev, 'name')}
                placeholder={t("folders.modal.type_something")}
                maxLength={75}
            />
        </FlexBox>
        <FlexBox gy={10} gyMobile={10} direction="column" directionMobile={'column'} justify={'flex-start'} justifyMobile={is744 ? 'center' : 'flex-start'}>
            <Availability
                availability={computeAvailability}
                direction="column"
                onChange={onAvailabilityChange}
                isFolder
                onCopy={handleLinkCopy}
                disableItem={!!folder?.isDefault ? 'link' : ''}
            />
            <PinCheckboxWrapper 
                style={{ position: 'relative' }} 
                direction="row" 
                directionMobile="row" 
                align="center"
                disable={computeAvailability === 'link'} 
                onClick={() => {
                    const fakeEvent = { target: { value: !folder?.isDefault }} as BaseSyntheticEvent
                    onDataChange(fakeEvent, 'isDefault')
                }}
            >
                <Input.Checkbox title={''} checked={folder?.isDefault}/>
                <span className={'checkbox-text'}>
                    {t("folders.modal.make_default")}
                    <InfoIcon stc={isMobile} msg={t("folders.hints.pin")} />
                </span>
            </PinCheckboxWrapper>
        </FlexBox>
        <FlexBox justify={folderId ? 'space-between' : "flex-end"} direction={isTab ? 'column' : 'row'} directionMobile={folderId ? 'column' : 'row'}  align="flex-end" alignMobile="center" >
            {folderId && (
                <SendButton fullWidth={isMobile} hollow red onClick={onDeleteFolder}>
                    {t("folders.modal.delete")}
                </SendButton>
            )}
            <FlexBox widthMobile={'100%'} direction={isTab ? 'column' : 'row'} directionMobile={'column'} gx={isTab || is744 || (isMobile && folderId) ? 0 : 16} gy={isTab ? 8 : 1} gyMobile={is744 || (isMobile && folderId) ? 8 : 0}>
                <SendButton hollow onClick={onCancel} fullWidth={Boolean(isMobile && folderId )|| isTab}>
                    {t("folders.modal.cancel")}
                </SendButton>
                <SendButton fill fullWidth={Boolean(isMobile && folderId) || isTab} isDisabled={isDisabled} onClick={() => isDisabled ? {} : onSubmit()}>
                    {t("folders.modal.save")}
                </SendButton>
            </FlexBox>
        </FlexBox>
    </Wrapper>
}import {
    Wrapper,
    SendButton,
    Subtitle,
    Title,
    PinCheckboxWrapper,
} from "./style";
import {FlexBox, useBreakpoint, useIsMobile, useToasts} from "@brg/ui";
import {act, BaseSyntheticEvent, FC, useCallback, useEffect, useMemo, useState} from "react";
import {API, Endpoints} from "@brg/api";
import {useTranslation} from "react-i18next";
import { ProfileUtils, useAssociatedProfile, useAuth } from "@brg/auth";
import {useSelector} from "react-redux";
import { Input } from "@brg/input";
import { LoaderIcon } from "shared/modals/artzLegalsModal/LoaderAnimation";
import { LayoutConst } from "core";
import { Availability } from "shared/pages/projectEdit/components/Availability/Availability";
import { AvailabilityAction } from "shared/pages/projectEdit/types";
import { copyLinkFolder } from "shared/helpers/copyLink";
import { useFoldersController } from "shared/hooks/useFoldersController";
import { ProfileFoldersProps, ProfileFoldersSelector } from "shared/reducers/ProfileFoldersSlice";
import { FOLDERS_MODE, FolderT } from "../../tabs/const";
import useQueryState from "shared/hooks/useQueryState";
import { InfoIcon } from "shared/components/InfoIcon/InfoIcon";

const defaultFolder = {
    name: '',
    accountId: '',
    description: '',
    isDefault: false,
    isHidden: false,
    isAvailableAtLink: AvailabilityAction.open,
} as FolderT

interface FolderModalProps {
    folderId?: string,
    activeFolder?: FolderT,
    callback?: (t: any) => void,
}

export const FolderEdit: FC<FolderModalProps> =
    ({folderId, activeFolder, callback }) => {
    const profile = useAssociatedProfile();
    const isMobile = useBreakpoint(LayoutConst.screen.m);
    const toasts = useToasts();
    const is744 = useBreakpoint(LayoutConst.screen.s - 1);
    const isTab = useBreakpoint(LayoutConst.screen.m);
    const { info } = useToasts();
    const {t, i18n} = useTranslation();
    const [{}, setQueryParams] = useQueryState();
    const currentFolder = useSelector(ProfileFoldersSelector)?.currentFolder;
    const [folder, setFolder] = useState<FolderT>(defaultFolder);
    const { 
        onFolderChange, 
        onCreate, 
        onEdit,
        onDelete 
    } = useFoldersController();

    const isDisabled = useMemo(() => folder?.name === '' , [folder])

    const onDataChange = (ev: BaseSyntheticEvent , field: string) => {
        setFolder({ ...folder, [field]: ev.target.value })

        if (activeFolder) {
            onEdit && onEdit({ ...folder, [field]: ev.target.value }, FOLDERS_MODE.edit, true).then((r) => {
                callback && callback(Date.now());
            })
        }
    }

    const onDeleteFolder = () => {

        onDelete(folder?.id ?? '', true)?.then(
            (r) => {
                callback && callback(Date.now())
                info(t('folders.modal.success_delete_folder', { name: folder.name }));
                onFolderChange(ProfileFoldersProps.projects)
                setQueryParams({ folderId: undefined })
            }
            
        )
    }

    const onAvailabilityChange = (val: string ) => {
        const compValue = val !== 'open';
        onDataChange({ target: { value: compValue }} as BaseSyntheticEvent, 'isHidden');
    }

    const onSubmit = () => {
        const data = {
            ...(folderId && { ...folder, id: folderId }),
            "name": folder.name,
            "description": '',
            "accountId": profile?.accountId,
            "isDefault": folder?.isDefault,
            "isHidden": folder?.isHidden
        } as FolderT

        if (currentFolder === ProfileFoldersProps.edit) {
            onEdit(data, FOLDERS_MODE.edit, true)
        } else {
            onCreate(data)
        }

        setTimeout(() => {
            info(t(`folders.modal.success_${folderId ? 'edit' : 'create'}`, { name: folder?.name || ''}));
            callback && callback(Date.now())
            onCancel && onCancel();
        } , 1000)

    }

    const onCancel = () => {
        onFolderChange(ProfileFoldersProps.projects)
    }

    const handleLinkCopy = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        copyLinkFolder(folder?.name, profile?.accountId || '', toasts, t);
    };

    useEffect(() => {
        if (activeFolder) {
            setFolder(activeFolder)
        }
    }, [activeFolder])

    const computeAvailability = useMemo(() => {
        if (!folder?.isHidden) return 'open';
        return 'link'
    } , [folder])

    return <Wrapper>
        <FlexBox justify="space-between" align={"center"} style={{marginRight: '5px', position: 'relative'}}>
            <Title>{folderId ?  t("folders.modal.edit_title") : t("folders.modal.title")}</Title>
        </FlexBox>
        <FlexBox direction="column" directionMobile="column" align={"start"} gy={8}>
            <Subtitle>{t("folders.modal.subtitle")}</Subtitle>
            <Input.Text 
                value={folder?.name ?? ''}
                onChange={(ev) => onDataChange(ev, 'name')}
                placeholder={t("folders.modal.type_something")}
                maxLength={75}
            />
        </FlexBox>
        <FlexBox gy={10} gyMobile={10} direction="column" directionMobile={'column'} justify={'flex-start'} justifyMobile={is744 ? 'center' : 'flex-start'}>
            <Availability
                availability={computeAvailability}
                direction="column"
                onChange={onAvailabilityChange}
                isFolder
                onCopy={handleLinkCopy}
                disableItem={!!folder?.isDefault ? 'link' : ''}
            />
            <PinCheckboxWrapper 
                style={{ position: 'relative' }} 
                direction="row" 
                directionMobile="row" 
                align="center"
                disable={computeAvailability === 'link'} 
                onClick={() => {
                    const fakeEvent = { target: { value: !folder?.isDefault }} as BaseSyntheticEvent
                    onDataChange(fakeEvent, 'isDefault')
                }}
            >
                <Input.Checkbox title={''} checked={folder?.isDefault}/>
                <span className={'checkbox-text'}>
                    {t("folders.modal.make_default")}
                    <InfoIcon stc={isMobile} msg={t("folders.hints.pin")} />
                </span>
            </PinCheckboxWrapper>
        </FlexBox>
        <FlexBox justify={folderId ? 'space-between' : "flex-end"} direction={isTab ? 'column' : 'row'} directionMobile={folderId ? 'column' : 'row'}  align="flex-end" alignMobile="center" >
            {folderId && (
                <SendButton fullWidth={isMobile} hollow red onClick={onDeleteFolder}>
                    {t("folders.modal.delete")}
                </SendButton>
            )}
            <FlexBox widthMobile={'100%'} direction={isTab ? 'column' : 'row'} directionMobile={'column'} gx={isTab || is744 || (isMobile && folderId) ? 0 : 16} gy={isTab ? 8 : 1} gyMobile={is744 || (isMobile && folderId) ? 8 : 0}>
                <SendButton hollow onClick={onCancel} fullWidth={Boolean(isMobile && folderId )|| isTab}>
                    {t("folders.modal.cancel")}
                </SendButton>
                <SendButton fill fullWidth={Boolean(isMobile && folderId) || isTab} isDisabled={isDisabled} onClick={() => isDisabled ? {} : onSubmit()}>
                    {t("folders.modal.save")}
                </SendButton>
            </FlexBox>
        </FlexBox>
    </Wrapper>
}